/*
 * The app tile in the product grid.
 *
 * An app block renders a container and nothing else. Atomz's discovery prompt
 * ships `<div class="az-discovery-prompts-container"></div>` plus a module
 * script, and az-global.js fills that container later, if it has prompts for
 * this page type at all. Server-side it is always empty.
 *
 * So the cell cannot be hidden with CSS. `:empty` does not match, because the
 * slot holds a script and a div; it just holds nothing you can see. Left
 * alone, an app with nothing to say leaves a product-sized hole in the middle
 * of the grid, which is worse than not having the tile.
 *
 * This measures instead: if nothing inside the slot has painted, the cell
 * takes itself out of the row, and it comes back the moment the app fills in.
 */
(function () {
  var IGNORE = { SCRIPT: 1, TEMPLATE: 1, STYLE: 1, LINK: 1, NOSCRIPT: 1 };

  function hasPaint(slot) {
    var kids = slot.querySelectorAll('*');
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (IGNORE[el.tagName]) continue;
      var r = el.getBoundingClientRect();
      if (r.width > 4 && r.height > 4) return true;
    }
    return false;
  }

  // Our own show and hide are mutations too, and the observer below would
  // otherwise answer them forever.
  var busy = false;

  function settle(slot) {
    busy = true;
    try {
      // Un-hide before measuring. A hidden cell is display: none, so every
      // descendant reports a zero box and the check could only ever answer
      // "still empty": the tile would latch shut and never come back when the
      // app finally filled it.
      if (slot.hidden) slot.hidden = false;

      var paint = hasPaint(slot);

      // The attribute alone is not enough. .az-card--slot sets display, and an
      // author rule beats the browser's own [hidden] { display: none }, so
      // atomz.css carries an explicit .az-card--slot[hidden] rule and this
      // line depends on it.
      slot.hidden = !paint;
    } finally {
      busy = false;
    }
  }

  function watch(slot) {
    settle(slot);

    var pending = null;
    var recheck = function () {
      if (pending) return;
      pending = requestAnimationFrame(function () {
        pending = null;
        settle(slot);
      });
    };

    // The app fills the container whenever its own fetch answers, so watch
    // rather than guess at a delay.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        // Our own hide and show are mutations too. Without this the observer
        // answers itself forever.
        if (!busy) recheck();
      }).observe(slot, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    // A backstop for an app that paints into a canvas or a shadow root, where
    // the observer above sees nothing.
    [600, 2000, 5000].forEach(function (ms) { setTimeout(recheck, ms); });
    window.addEventListener('load', recheck);
  }

  /*
   * Move the rendered app block into the grid cell.
   *
   * The section renders it off-screen, where Shopify is happy to render an app
   * block, and it lands here before the app has painted. Moving a node does
   * not reset it: az-global.js keeps its reference to the same container and
   * fills it in place, so the greeting and the prompts the merchant configured
   * in the app admin arrive in the cell rather than in a copy of it.
   */
  function adopt() {
    var slot = document.querySelector('[data-az-slot]');
    var source = document.querySelector('[data-az-slot-source]');
    if (!slot || !source) return;

    while (source.firstChild) slot.appendChild(source.firstChild);
    source.parentNode.removeChild(source);
  }

  function start() {
    adopt();
    var slots = document.querySelectorAll('.az-card--slot');
    for (var i = 0; i < slots.length; i++) watch(slots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
